import { cn } from "@/lib/utils";
import {
  DAY_HEADERS,
  MONTHS_FULL,
  isBetween,
  sameDay,
  toMidnight,
} from "@/lib/date";

interface MonthPanelProps {
  year: number;
  month: number;
  startDate: Date | null;
  endDate: Date | null;
  hoverDate: Date | null;
  onDayClick: (date: Date) => void;
  onDayHover: (date: Date | null) => void;
  onDrillDown: (year: number, month: number) => void;
}

/** Single-month calendar grid with row-by-row range highlighting. */
export function MonthPanel({
  year,
  month,
  startDate,
  endDate,
  hoverDate,
  onDayClick,
  onDayHover,
  onDrillDown,
}: MonthPanelProps) {
  const today = toMidnight(new Date())!;
  const firstDayOfWeek = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const cells: (number | null)[] = [];
  for (let i = 0; i < firstDayOfWeek; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length < 42) cells.push(null);

  const rangeEnd = endDate ?? hoverDate;
  const isDotted = !endDate && !!hoverDate;

  const rows: React.ReactNode[] = [];
  for (let row = 0; row < 6; row++) {
    const rowCells = cells.slice(row * 7, row * 7 + 7);
    if (rowCells.every((c) => c === null)) continue;

    // Find leftmost and rightmost in-range cell in this row.
    let rS: number | null = null;
    let rE: number | null = null;
    rowCells.forEach((day, col) => {
      if (!day) return;
      const date = toMidnight(new Date(year, month, day))!;
      const inRange =
        !!(startDate && rangeEnd) &&
        (sameDay(date, toMidnight(startDate)) ||
          sameDay(date, toMidnight(rangeEnd)) ||
          isBetween(date, toMidnight(startDate), toMidnight(rangeEnd)));
      if (inRange) {
        if (rS === null) rS = col;
        rE = col;
      }
    });

    const lDay = rS !== null ? rowCells[rS] : null;
    const rDay = rE !== null ? rowCells[rE] : null;
    const lIsStart =
      lDay !== null && sameDay(toMidnight(new Date(year, month, lDay!)), toMidnight(startDate));
    const rIsEnd =
      rDay !== null &&
      ((endDate && sameDay(toMidnight(new Date(year, month, rDay!)), toMidnight(endDate))) ||
        (!endDate &&
          hoverDate &&
          sameDay(toMidnight(new Date(year, month, rDay!)), toMidnight(hoverDate))));

    const dayCells = rowCells.map((day, col) => {
      if (day === null) return <div key={col} />;
      const date = toMidnight(new Date(year, month, day))!;
      const isStart = sameDay(date, toMidnight(startDate));
      const isEnd = !!(endDate && sameDay(date, toMidnight(endDate)));
      const isHov = !!(!endDate && sameDay(date, toMidnight(hoverDate)));
      const isToday = sameDay(date, today);
      const hl = isStart || isEnd || isHov;

      return (
        <div
          key={col}
          onMouseEnter={() => onDayHover(date)}
          onClick={() => onDayClick(date)}
          className="relative z-10 flex h-9 cursor-pointer items-center justify-center"
        >
          <div
            className={cn(
              "flex h-[34px] w-[34px] items-center justify-center rounded-full text-[13px] transition-colors",
              hl
                ? "bg-brand font-bold text-white"
                : "text-foreground hover:bg-brand-light/40 hover:text-brand",
              !hl && isToday && "border-[1.5px] border-brand text-brand",
              !hl && !isToday && "border-[1.5px] border-transparent",
            )}
          >
            {day}
          </div>
        </div>
      );
    });

    rows.push(
      <div
        key={row}
        className="relative mb-0.5 grid grid-cols-7"
      >
        {rS !== null && rE !== null && (
          <div
            className={cn(
              "pointer-events-none absolute inset-y-0.5 z-0",
              isDotted
                ? // Hover preview while picking the end date — tentative but visible.
                  "bg-brand-light/60 border-[1.5px] border-dashed border-brand/40"
                : // Committed range — opaque tint so the band reads as a clear span.
                  "bg-brand-light",
            )}
            style={{
              left: `${(rS / 7) * 100}%`,
              right: `${((6 - rE) / 7) * 100}%`,
              borderTopLeftRadius: lIsStart ? 17 : 0,
              borderBottomLeftRadius: lIsStart ? 17 : 0,
              borderTopRightRadius: rIsEnd ? 17 : 0,
              borderBottomRightRadius: rIsEnd ? 17 : 0,
            }}
          />
        )}
        {dayCells}
      </div>,
    );
  }

  return (
    <div className="flex-1">
      <div className="mb-2.5 text-center">
        <button
          type="button"
          onClick={() => onDrillDown(year, month)}
          className="rounded-md px-3 py-1 text-[15px] font-bold text-foreground hover:bg-accent"
        >
          {MONTHS_FULL[month]} {year}
        </button>
      </div>
      <div className="mb-1 grid grid-cols-7">
        {DAY_HEADERS.map((d, i) => (
          <div
            key={i}
            className="px-0 pb-1.5 pt-0.5 text-center text-[11px] font-semibold text-muted-foreground"
          >
            {d}
          </div>
        ))}
      </div>
      {rows}
    </div>
  );
}
